import type { ListBuilderColumn } from '../../types';
import { MERCHANDISER_STATUS_VALUE_MAPPING } from './shared';

/** Merchandiser name (link to detail) with manufacturer/model subtext. */
export const createMerchandiserNameColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-name',
	headers: [{ id: 'merchandiser-name-header', text: 'Merchandiser' }],
	fields: [
		{
			id: 'name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			// PD-14339 — managed-catalog make/model shown together as subtext
			// (e.g. "True TDD-2"). Was the dropped free-text `manufacturer` field.
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'manufacturerName', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
		{
			id: 'make-model-space',
			dataConfig: { type: 'custom_text', customText: ' ' },
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
		{
			id: 'model-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'modelName', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
		// PD-15485 — the serial plate, on its own line under make/model. It is
		// how a unit is identified in the field, and the list could neither
		// show it nor be searched by it. Hidden entirely when absent: units are
		// regularly received before their serial plate is available, and a
		// bare "S/N" label on an empty value reads as missing data.
		{
			id: 'serial-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: {
				field: 'serialNumber',
				condition: 'is_not_null',
			},
		},
		{
			id: 'serial-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'serialNumber', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
			prefix: 'S/N ',
			conditionalVisibility: {
				field: 'serialNumber',
				condition: 'is_not_null',
			},
		},
	],
	sortable: true,
});

/**
 * PD-15485 — the sensor hardware attached to the unit, by the device's own
 * id (the label printed on it), so a row can be matched to hardware in hand.
 */
export const createSensorDeviceColumn = (): ListBuilderColumn => ({
	id: 'sensor-device',
	headers: [{ id: 'sensor-device-header', text: 'Sensor' }],
	fields: [
		{
			id: 'sensor-device-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'sensorDeviceLabel', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
			emptyValue: '-',
		},
	],
	sortable: false,
});

/** Placement — the customer / address the unit sits at (or warehouse). */
export const createPlacementColumn = (): ListBuilderColumn => ({
	id: 'placement',
	headers: [{ id: 'placement-header', text: 'Placement' }],
	fields: [
		{
			id: 'placement-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'locationInfo', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	minWidth: 220,
});

/** Status badge. */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status',
	headers: [{ id: 'status-header', text: 'Status' }],
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'status', objectTable: 'merchandiser' },
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [...MERCHANDISER_STATUS_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	sortable: true,
	width: 130,
});

/**
 * Number of locations (child locations) the merchandiser owns. A unit with
 * none configured shows a dash — "0" reads as a measured value on a unit we
 * actually know nothing about (owner request, 2026-08-24).
 */
export const createBinsColumn = (): ListBuilderColumn => ({
	id: 'bins',
	headers: [{ id: 'bins-header', text: 'Locations' }],
	fields: [
		{
			id: 'bins-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'locationsCount',
						// Compared as strings by the conditional resolver.
						operator: 'not_equals',
						value: '0',
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'locationsCount',
								objectTable: 'merchandiser',
							},
						},
					},
				],
				defaultDataConfig: { type: 'custom_text', customText: '—' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 90,
});

/**
 * Fill % rolled up across the merchandiser's locations. Null (no sized bins
 * → unknowable) renders a dash. The former separate Stock column is folded
 * into the leading warning icon: shown when the unit needs a refill
 * (stock Empty/Low), keyed off the row's `refillNeeded` flag.
 */
export const createFillColumn = (): ListBuilderColumn => ({
	id: 'fill',
	headers: [{ id: 'fill-header', text: 'Fill %' }],
	fields: [
		{
			id: 'fill-warning-icon',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'sm',
				iconName: 'triangle-exclamation',
				colorRule: { type: 'static', staticColor: { type: 'warning' } },
				tooltip: 'Needs refill',
			},
			displayStyle: 'icon',
			conditionalVisibility: {
				field: 'refillNeeded',
				condition: 'is_not_null',
			},
			floatingLeft: true,
		},
		{
			id: 'fill-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'fillPercentage',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'fillPercentage',
								objectTable: 'merchandiser',
							},
						},
					},
				],
				defaultDataConfig: { type: 'custom_text', customText: '—' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 100,
});

/**
 * PD-15416 — the unit's average zone temperature (°F, bubbled from the
 * attached sensor device's depth modules at ingestion). Blank when the
 * merchandiser has no reporting sensors.
 */
export const createTemperatureColumn = (): ListBuilderColumn => ({
	id: 'temperature',
	headers: [{ id: 'temperature-header', text: 'Temp' }],
	fields: [
		{
			// The former Alerts column, folded in: an open sensor excursion
			// (temperature/offline) lights a warning icon before the reading;
			// the alert detail stays on the unit's Issues tab.
			id: 'temperature-warning-icon',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'sm',
				iconName: 'triangle-exclamation',
				colorRule: { type: 'static', staticColor: { type: 'warning' } },
				tooltip: 'Open sensor alert',
			},
			displayStyle: 'icon',
			conditionalVisibility: {
				field: 'alertSummary',
				condition: 'is_not_null',
			},
			floatingLeft: true,
		},
		{
			id: 'temperature-field',
			dataConfig: {
				// A bare template renders a dangling "°F" when there is no
				// reading — the presence-checked conditional shows "—" instead.
				type: 'conditional',
				conditions: [
					{
						field: 'currentTemperature',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'template',
							template: '{merchandiser.currentTemperature}°F',
							templateFields: [
								{
									fieldName: 'currentTemperature',
									objectTable: 'merchandiser',
								},
							],
						},
					},
				],
				defaultDataConfig: { type: 'custom_text', customText: '—' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 80,
});

/**
 * PD-15417 — open sensor-alert badge. `alertSummary` is only set when the
 * unit has an open excursion ("Temperature", "Offline", "2 alerts"), so a
 * presence check lights the cell; healthy rows stay blank.
 */
export const createAlertsColumn = (): ListBuilderColumn => ({
	id: 'alerts',
	headers: [{ id: 'alerts-header', text: 'Alerts' }],
	fields: [
		{
			id: 'alerts-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'alertSummary',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: { fieldName: 'alertSummary', objectTable: 'merchandiser' },
						},
					},
				],
				defaultDataConfig: { type: 'custom_text', customText: '' },
			},
			styleConfig: {
				size: 'sm',
				fontWeight: 'medium',
				colorRule: { type: 'static', staticColor: { type: 'warning' } },
			},
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

/**
 * PD-15733 — managed sensor-driven health badge. Derived server-side from
 * the unit's open alerts + device attachment: an open offline alert wins,
 * any other open alert needs attention, a sensored unit with a clean slate
 * is healthy, and a unit with no sensor shows its own quiet state.
 */
export const createHealthColumn = (): ListBuilderColumn => ({
	id: 'health',
	headers: [{ id: 'health-header', text: 'Health' }],
	fields: [
		{
			id: 'health-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'healthState', objectTable: 'merchandiser' },
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [
					{
						value: 'healthy',
						label: 'Healthy',
						color: { type: 'success' },
					},
					{
						value: 'needs_attention',
						label: 'Needs Attention',
						color: { type: 'warning' },
					},
					{
						value: 'offline',
						label: 'Offline',
						color: { type: 'error' },
					},
				],
			},
			displayStyle: 'badge',
		},
	],
	sortable: false,
	width: 140,
});

/** Stock level (Empty / Low / Medium / Full). */
export const createStockLevelColumn = (): ListBuilderColumn => ({
	id: 'stock-level',
	headers: [{ id: 'stock-level-header', text: 'Stock' }],
	fields: [
		{
			id: 'stock-level-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'stockLevel', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

// Alerts + Stock dropped as separate columns (owner request, 2026-08-24):
// their signal now rides as warning icons on Temp and Fill %, so the table
// fits without horizontal scrolling. The factories above remain exported for
// saved layouts that still reference them.
export const createMerchandiserColumns = (): Array<ListBuilderColumn> => [
	createMerchandiserNameColumn(),
	createPlacementColumn(),
	createStatusColumn(),
	// PD-15733 — the sensor-driven health state, beside the unit status so
	// "Active but blind" reads at a glance.
	createHealthColumn(),
	createSensorDeviceColumn(),
	createBinsColumn(),
	createFillColumn(),
	createTemperatureColumn(),
];
